import React, { useState, useEffect } from 'react';
import {
  Button,
  Field,
  Input,
  Text,
  Caption1,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  MessageBar,
  MessageBarBody,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Add24Regular,
  Delete24Regular,
} from '@fluentui/react-icons';
import { useMsalAuth } from '../../hooks/useMsalAuth';
import { useEnvironment } from '../../contexts/AppContext';
import { AliasesService } from '../../services/api/aliasesService';
import type { AliasObject } from '../../types/trapi';

const useStyles = makeStyles({
  section: { display: 'grid', gap: tokens.spacingVerticalS },
  accordionContent: { display: 'grid', gap: tokens.spacingVerticalM },
  headerRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  loadingRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  aliasesList: { display: 'grid', gap: tokens.spacingVerticalS },
  aliasItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalS,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  addRow: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'flex-end' },
});

export interface AliasSectionProps {
  apiPath: string;
  disabled?: boolean;
}

export const AliasSection: React.FC<AliasSectionProps> = ({ apiPath, disabled = false }) => {
  const styles = useStyles();
  const { currentEnvironment } = useEnvironment();
  const { getToken, account } = useMsalAuth();

  const [aliases, setAliases] = useState<AliasObject[]>([]);
  const [newAliasPath, setNewAliasPath] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState<string>('');

  const loadAliases = async () => {
    if (!apiPath.trim()) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const token = await getToken();
      const fetchedAliases = await AliasesService.getAliases({
        apiPath,
        environment: currentEnvironment,
        accessToken: token,
      });
      setAliases(fetchedAliases);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load aliases');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAliases();
  }, [apiPath, currentEnvironment]);

  const handleAddAlias = async () => {
    if (!newAliasPath.trim()) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const token = await getToken();
      const newAlias = await AliasesService.createAlias({
        aliasPath: newAliasPath.trim(),
        apiPath,
        environment: currentEnvironment,
        accessToken: token,
      });
      
      setAliases(prev => [...prev, newAlias]);
      setNewAliasPath('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create alias');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAlias = async (aliasId: string) => {
    setIsLoading(true);
    setError('');
    
    try {
      const token = await getToken();
      await AliasesService.deleteAlias({
        id: aliasId,
        environment: currentEnvironment,
        accessToken: token,
      });
      
      setAliases(prev => prev.filter(alias => alias.id !== aliasId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete alias');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className={styles.section}>
      <Accordion collapsible>
        <AccordionItem value="aliases">
          <AccordionHeader
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className={styles.headerRow}>
              <Text weight="semibold">Aliases</Text>
              <Caption1>({aliases.length})</Caption1>
            </div>
          </AccordionHeader>
          <AccordionPanel>
            <div className={styles.accordionContent}>
              {error && (
                <MessageBar intent="error">
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              )}

              {isLoading && (
                <div className={styles.loadingRow}>
                  <Spinner size="tiny" />
                  <Caption1>Loading aliases...</Caption1>
                </div>
              )}

              {/* Existing aliases */}
              {aliases.length > 0 && (
                <div className={styles.aliasesList}>
                  {aliases.map((alias) => (
                    <div
                      key={alias.id}
                      className={styles.aliasItem}
                    >
                      <Text>{alias.aliasPath}</Text>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<Delete24Regular />}
                        onClick={() => handleDeleteAlias(alias.id)}
                        disabled={disabled || isLoading}
                        title="Delete alias"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Add new alias */}
              <div className={styles.addRow}>
                <Field label="Add New Alias" style={{ flex: 1 }}>
                  <Input
                    value={newAliasPath}
                    onChange={(_, data) => setNewAliasPath(data.value)}
                    placeholder="Enter alias path (e.g., /api/v1/alias)"
                    disabled={disabled || isLoading}
                  />
                </Field>
                <Button
                  appearance="outline"
                  icon={<Add24Regular />}
                  onClick={handleAddAlias}
                  disabled={disabled || isLoading || !newAliasPath.trim()}
                >
                  Add
                </Button>
              </div>

              <Caption1>
                Aliases provide alternative paths to access this API. Each alias path will route to the same API endpoint.
              </Caption1>
            </div>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </section>
  );
};

export default AliasSection;
